import React, { useMemo, useState } from 'react';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import {
  selectGroup,
  selectIncident,
  selectUser,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { getUserDisplayName } from 'utils/userDisplayName';
import { useDisasterChats } from './hooks/useDisasterChats';
import { useCanViewSharedChats } from './hooks/useCanViewSharedChats';
import type { DisasterChatSummary } from './chatTypes';

export type ChatScope =
  | { scope: 'workspace' }
  | { scope: 'self' }
  | { scope: 'user'; ownerGuid: string; ownerLabel: string };

export const SELF_SCOPE: ChatScope = { scope: 'self' };

type ScopeOption = {
  value: string;
  label: string;
  ownerGuid?: string;
};

type ChatRow = Pick<
  DisasterChatSummary,
  'user_guid' | 'owner_name' | 'owner_email'
>;

const WORKSPACE_VALUE = '__workspace__';
const SELF_VALUE = '__self__';

/** Display label for a chat's owner (shared name → email fallback). */
export const chatOwnerLabel = (chat: ChatRow): string =>
  getUserDisplayName(chat);

interface ChatScopeSelectorProps {
  value: ChatScope;
  onChange: (scope: ChatScope) => void;
}

/**
 * Whose chats the list shows. Option order is fixed: the whole scope first
 * (incident or org), then the viewer themselves — labelled with the shared
 * name-or-email helper plus "(you)" rather than a possessive — then the other
 * owners present in the workspace listing, alphabetical descending. Deriving
 * teammates from the listing keeps former members whose chats remain browsable
 * and never shows pending org members. Renders nothing for users without
 * `incident:viewSharedChats`. Chats that are not the viewer's own open
 * read-only.
 */
const ChatScopeSelector: React.FC<ChatScopeSelectorProps> = ({
  value,
  onChange,
}) => {
  const canViewShared = useCanViewSharedChats();
  const myGuid = useAppSelector(selectUserGuid);
  const me = useAppSelector(selectUser);
  const group = useAppSelector(selectGroup);
  const incident = useAppSelector(selectIncident);

  // The workspace listing feeds the teammate options. It is fetched lazily —
  // once the menu has been opened, or while a shared scope is active — so the
  // default self-scoped view does not pay for (and re-poll) the whole
  // workspace's chats: every agent poll tick invalidates the chat-list key.
  const [menuOpened, setMenuOpened] = useState(false);
  const { data: workspaceChats = [] } = useDisasterChats(
    { scope: 'workspace' },
    { enabled: canViewShared && (menuOpened || value.scope !== 'self') }
  );

  const options = useMemo<ScopeOption[]>(() => {
    const workspaceName = incident?.name || group?.group_name || 'Workspace';
    const owners = new Map<string, string>();
    for (const chat of workspaceChats as ChatRow[]) {
      if (
        chat.user_guid &&
        chat.user_guid !== myGuid &&
        !owners.has(chat.user_guid)
      ) {
        owners.set(chat.user_guid, chatOwnerLabel(chat));
      }
    }
    // Descending alphabetical (Z → A) per product decision 2026-09-14.
    const memberOptions = Array.from(owners.entries())
      .map(([ownerGuid, label]) => ({ value: ownerGuid, label, ownerGuid }))
      .sort((a, b) =>
        b.label.localeCompare(a.label, undefined, { sensitivity: 'base' })
      );

    return [
      { value: WORKSPACE_VALUE, label: `All — ${workspaceName}` },
      { value: SELF_VALUE, label: `${getUserDisplayName(me)} (you)` },
      ...memberOptions,
    ];
  }, [workspaceChats, myGuid, me, incident?.name, group?.group_name]);

  if (!canViewShared) return null;

  const selectedValue =
    value.scope === 'workspace'
      ? WORKSPACE_VALUE
      : value.scope === 'self'
        ? SELF_VALUE
        : value.ownerGuid;
  const selectedOption =
    options.find((opt) => opt.value === selectedValue) ??
    (value.scope === 'user'
      ? { value: value.ownerGuid, label: value.ownerLabel }
      : undefined);

  return (
    <StylishNewSelect
      classes="chat-scope-selector"
      options={options}
      value={selectedOption}
      onChange={(option: ScopeOption) => {
        if (option.value === WORKSPACE_VALUE) onChange({ scope: 'workspace' });
        else if (option.value === SELF_VALUE) onChange(SELF_SCOPE);
        else
          onChange({
            scope: 'user',
            ownerGuid: option.value,
            ownerLabel: option.label,
          });
      }}
      onMenuOpen={() => setMenuOpened(true)}
      isSearchable={false}
      isClearable={false}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      styles={{
        menuPortal: (base: Record<string, unknown>) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default ChatScopeSelector;
